import { Unsubscribe } from "firebase/auth";
import UserModel from "../models/UserModel";
import AuthRepository from "../repositories/AuthRepository";
import UserService from "./UserService";
import { InvalidUserException } from "../exceptions/AuthException";
import { RequiredParamException } from "../exceptions/InvalidParamException";
import UserRepository from "api/repositories/UserRepository";

class AuthService {
  private authSnapshot: Unsubscribe | undefined;
  private userSnapshot: Unsubscribe | undefined;
  private _onSignChangeSession: Function | undefined;
  private _onSignAnotherSession: Function | undefined;
  private _onSignErrorSession: Function | undefined;

  constructor() {
    // Add um Listener para verificar as sessoes do usuario logado
    this.createAuthSnapshot();
  }

  get currentUser(): UserModel | null {
    return AuthRepository.currentUser;
  }

  get currentUserId(): string {
    return AuthRepository.currentUserId;
  }

  get isAuthenticated(): boolean {
    return AuthRepository.isAuthenticated;
  }

  async createUserAccount(
    name: string,
    email: string,
    password: string
  ): Promise<UserModel | null> {
    const user = await AuthRepository.createAccountWithEmail(
      name,
      email,
      password
    );

    if (user) {
      user.setPermissionToFaturamento();
      await UserService.addUser(user);
    }

    return user;
  }

  updateEmail(email: string) {
    if (!email) throw new RequiredParamException("email");
    return AuthRepository.updateCurrentUserEmail(email);
  }

  async signInWithEmail(email: string, password: string): Promise<boolean> {
    if (this.isAuthenticated) return true;
    return AuthRepository.signInWithEmail(email, password);
  }

  signOutUser(): Promise<void> {
    this.clearUserSnapshot();
    this.clearSessionEvents();
    return AuthRepository.signOutUser();
  }

  sendPasswordResetEmail(email: string): Promise<void> {
    return AuthRepository.sendPasswordResetEmail(email);
  }

  sendVerificationEmail(): Promise<void> {
    return AuthRepository.sendVerificationEmailToCurrentUser();
  }

  async validateUser(user: UserModel) {
    if (user.products?.faturamento) return true;
    throw new InvalidUserException();
  }

  subscribeAuthObserver(
    onChange: (user: UserModel | null) => void,
    onSignNewSession?: Function,
    onError?: Function
  ): void {
    this._onSignChangeSession = onChange;
    this._onSignAnotherSession = onSignNewSession;
    this._onSignErrorSession = onError;
  }

  private createAuthSnapshot(): void {
    this.clearAuthSnapshot();
    this.authSnapshot = AuthRepository.subscribeAuthObserver(
      (userAuthenticated) => {
        return this.updateCurrentSession(userAuthenticated);
      },
      (error) => {
        console.error(error);
        this.dispachSessionError(error);
      }
    );
  }

  /**
    Esta função atualiza a sessao do usuario atual e adiciona um listener ao mesmo
    para monitorar suas sessoes. Caso ele entre em uma nova sessao,
    o mesmo sera deslogado da sessao atual.
  */
  private async updateCurrentSession(user: UserModel): Promise<void> {
    this.dispachSessionChange(user);
    if (!user) return;

    if (!user.uid || !user.email) {
      throw new Error("User is not defined.");
    }

    const token = user.token;
    await UserService.updateSessionData(user, token);

    // Add o subscribe para verifica se o usuario se loga em uma nova sessao
    this.clearUserSnapshot();
    this.userSnapshot = UserRepository.subscribeUserSnapshot(
      user.uid,
      async (userData) => {
        const lastToken = userData?.lastSignOnFaturamento?.token;

        if (userData && token !== lastToken) {
          await this.dispachSessionNew();
        }

        if (!userData.products?.faturamento) {
          await this.dispachSessionError(new InvalidUserException());
        }
      }
    );
  }

  private dispachSessionChange(user: UserModel) {
    if (this._onSignChangeSession) {
      this._onSignChangeSession(user);
    }
  }

  private async dispachSessionNew() {
    if (this._onSignAnotherSession) {
      await this._onSignAnotherSession();
    }
  }

  private async dispachSessionError(error: Error) {
    if (this._onSignErrorSession) {
      await this._onSignErrorSession(error);
    }
  }

  private clearUserSnapshot(): void {
    if (this.userSnapshot) {
      this.userSnapshot();
    }
  }

  private clearAuthSnapshot(): void {
    if (this.authSnapshot) {
      this.authSnapshot();
    }
  }

  private clearSessionEvents() {
    this._onSignChangeSession = undefined;
    this._onSignAnotherSession = undefined;
    this._onSignErrorSession = undefined;
  }
}

export default new AuthService();
